import { lazy, Suspense, useCallback, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { useAuth, useAuthStatus } from '../auth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { trackFunnelStep, FunnelNames } from '@/services/telemetryFunnels';
import {
  LandingNav,
  HeroSection,
  TrustStrip,
  CtaSection,
  LandingFooter,
  VerifyingAccessOverlay,
} from './Landing';

// Below-the-fold marketing sections are lazy-loaded so the hero +
// trust strip + CTA can render with the smallest possible JS budget.
// We deliberately keep CtaSection eager because it is the canonical
// authentication entry point and must be ready when the keyboard user
// tabs to the bottom of the page.
const LowBarrierSection = lazy(() =>
  import('./Landing/LowBarrierSection').then((m) => ({ default: m.LowBarrierSection })),
);
const DesignAndCodeSection = lazy(() =>
  import('./Landing/DesignAndCodeSection').then((m) => ({ default: m.DesignAndCodeSection })),
);
const ManagedEnterpriseSection = lazy(() =>
  import('./Landing/ManagedEnterpriseSection').then((m) => ({ default: m.ManagedEnterpriseSection })),
);
const HumanInLoopSection = lazy(() =>
  import('./Landing/HumanInLoopSection').then((m) => ({ default: m.HumanInLoopSection })),
);
const NoQuotaSection = lazy(() =>
  import('./Landing/NoQuotaSection').then((m) => ({ default: m.NoQuotaSection })),
);
const FeaturesGrid = lazy(() =>
  import('./Landing/FeaturesGrid').then((m) => ({ default: m.FeaturesGrid })),
);
const ConnectorsSection = lazy(() =>
  import('./Landing/ConnectorsSection').then((m) => ({ default: m.ConnectorsSection })),
);
const ScenarioSection = lazy(() =>
  import('./Landing/ScenarioSection').then((m) => ({ default: m.ScenarioSection })),
);
const EnterpriseGridSection = lazy(() =>
  import('./Landing/EnterpriseGridSection').then((m) => ({ default: m.EnterpriseGridSection })),
);

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  page: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    overflowX: 'hidden',
  },
  // `<main>` needs to be programmatically focusable so the skip-link
  // can land the keyboard caret inside it without a real interactive
  // child. `tabIndex={-1}` is applied directly on the element.
  mainFocusTarget: {
    outlineStyle: 'none',
  },
});

export const LoginPage = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const { isVerifyingAuth } = useAuthStatus();
  const location = useLocation();
  const styles = useStyles();

  useDocumentTitle(['Sign in']);

  const fromLocation = (location.state as { from?: { pathname: string; search?: string } })?.from;
  const from = fromLocation ? `${fromLocation.pathname}${fromLocation.search ?? ''}` : '/';

  // Save the intended return URL to sessionStorage immediately when landing on login page.
  // This ensures the URL is preserved across the MSAL redirect flow, even if opened from another tab.
  useEffect(() => {
    if (fromLocation && from !== '/') {
      sessionStorage.setItem('authReturnUrl', from);
    }
  }, [fromLocation, from]);

  // CTAs pass a `source` string so we can attribute conversion to the
  // location on the page (nav / hero / final card / mobile menu) rather
  // than collapsing every login click into one anonymous funnel step.
  const handleLogin = useCallback(
    (source?: string) => {
      trackFunnelStep(FunnelNames.ONBOARDING, 'login', 0, source ? { source } : undefined);
      login();
    },
    [login],
  );

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Checking authentication..." />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <div className={styles.page}>
      <div id="login-page-content">
        <LandingNav onLogin={handleLogin} isVerifyingAuth={isVerifyingAuth} />
        <main id="main-content" tabIndex={-1} className={styles.mainFocusTarget}>
          <HeroSection onLogin={handleLogin} isVerifyingAuth={isVerifyingAuth} />
          <TrustStrip />
          <Suspense fallback={null}>
            <LowBarrierSection />
            <DesignAndCodeSection />
            <ManagedEnterpriseSection />
            <HumanInLoopSection />
            <NoQuotaSection />
            <FeaturesGrid />
            <ConnectorsSection />
            <ScenarioSection />
            <EnterpriseGridSection />
          </Suspense>
          <CtaSection onLogin={handleLogin} isVerifyingAuth={isVerifyingAuth} />
        </main>
        <LandingFooter />
      </div>
      {isVerifyingAuth && <VerifyingAccessOverlay />}
    </div>
  );
};
